import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { API_URL } from '../services/api.tokens';
import { AUTH_RETRIED_AFTER_REFRESH, AUTH_SESSION_BOOTSTRAP } from './auth.context';
import { AuthStore } from '@core/stores/auth.store';
import { TenantContextStore } from '@core/tenant/tenant-context.store';

function isAuthEndpoint(url: string): boolean {
  const path = url.split('?')[0];
  return (
    path.endsWith('/login') ||
    path.endsWith('/login/two-factor') ||
    path.endsWith('/login/two-factor/resend') ||
    path.endsWith('/refresh') ||
    path.endsWith('/signout')
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const apiUrl = inject(API_URL);
  const auth = inject(AuthStore);
  const tenantContext = inject(TenantContextStore);

  const apiRequest = req.url.startsWith(apiUrl);
  const activeTenantId = tenantContext.currentTenantId();

  let headers = req.headers;
  if (apiRequest && activeTenantId) {
    headers = headers.set('X-Tenant-Id', activeTenantId);
  }

  const outgoing = apiRequest ? req.clone({ withCredentials: true, headers }) : req;

  return next(outgoing).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }
      if (!apiRequest || isAuthEndpoint(req.url) || req.context.get(AUTH_SESSION_BOOTSTRAP)) {
        return throwError(() => error);
      }
      if (req.context.get(AUTH_RETRIED_AFTER_REFRESH)) {
        return throwError(() => error);
      }

      const retriedRequest = outgoing.clone({
        context: outgoing.context.set(AUTH_RETRIED_AFTER_REFRESH, true),
      });

      return auth.refreshSession().pipe(
        switchMap(() => next(retriedRequest)),
        catchError((refreshError) => {
          auth.clearSession();
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};
